import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { FormulaCardComponent } from '../formula-card/formula-card.component';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { validarExpressaoFormula } from '../utils/formula-expressao.util';
import { FormulaEditorDialogData } from './formula-editor-dialog-data';
import { FormulaEditorDialogResult } from './formula-editor-dialog-result';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { TipoDadoVariavelEnum } from '@shared/enums/formula/tipo-dado-variavel.enum';
import { FormulaCardModoEnum } from '@shared/enums/motor-fiscal/formula-card-modo.enum';
import { FORMULA_BADGE_CLASSES } from '../utils/formula-badge.util';
import { FormulaCatalogoValidarPersonalizacoesRequest } from '@shared/services/requests/formula/formula-catalogo-validar-personalizacoes.request';
import { FormulaCatalogoPersonalizacaoRequest } from '@shared/services/requests/formula/formula-catalogo-personalizacao.request';
import { FormulaErrorCodeEnum } from '@shared/enums/formula/formula-error-code.enum';
import { FormulaCatalogoErroStruct } from '@shared/services/structs/formula/formula-catalogo-erro.struct';
import { FormulaCicloDetectadoStruct } from '@shared/services/structs/formula/formula-ciclo-detectado.struct';

export { FormulaEditorDialogData } from './formula-editor-dialog-data';
export { FormulaEditorDialogResult } from './formula-editor-dialog-result';

@Component({
    selector: 'app-formula-editor-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        AlertCardComponent,
        FormulaCardComponent,
        EmptyStateComponent,
    ],
    templateUrl: './formula-editor-dialog.component.html',
    styleUrl: './formula-editor-dialog.component.css',
})
export class FormulaEditorDialogComponent implements OnInit {
    public readonly badgeClasses = FORMULA_BADGE_CLASSES;
    public readonly modoEditor = FormulaCardModoEnum.Editor;
    public estadosLocais: FormulaItemStruct[] = [];
    public isValidando = false;

    public get personalizadasCount(): number {
        return this.estadosLocais.filter(e => e.tipo === TipoFormulaEnum.Personalizada).length;
    }

    /** Habilita o salvamento quando há qualquer alteração (troca de modelo ou personalização). */
    public get temAlteracoes(): boolean {
        return this.estadosLocais.some(estado => !this.mesmoEstado(estado, this.estadoInicialDe(estado.chaveFormula)));
    }

    public get formulasPersonalizaveis(): FormulaCatalogoItemStruct[] {
        return this.data.formulas.filter(f => f.isPersonalizavel);
    }

    constructor(
        public dialogRef: MatDialogRef<FormulaEditorDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: FormulaEditorDialogData,
        private alertService: AlertService,
    ) {}

    ngOnInit(): void {
        this.estadosLocais = this.formulasPersonalizaveis.map(f => {
            const encontrado = this.data.estadosIniciais.find(e => e.chaveFormula === f.chaveFormula);
            return encontrado ?? { chaveFormula: f.chaveFormula, tipo: TipoFormulaEnum.ModeloPadrao };
        });
    }

    public getEstado(chaveFormula: string): FormulaItemStruct | null {
        return this.estadosLocais.find(e => e.chaveFormula === chaveFormula) ?? null;
    }

    /** Estado inicial da fórmula, com o mesmo fallback usado na inicialização. */
    private estadoInicialDe(chaveFormula: string): FormulaItemStruct {
        return this.data.estadosIniciais.find(e => e.chaveFormula === chaveFormula)
            ?? { chaveFormula, tipo: TipoFormulaEnum.ModeloPadrao };
    }

    /** Compara dois estados considerando apenas os campos relevantes para cada tipo. */
    private mesmoEstado(a: FormulaItemStruct, b: FormulaItemStruct): boolean {
        if (a.tipo !== b.tipo) return false;

        switch (a.tipo) {
            case TipoFormulaEnum.ModeloPadrao:
                return true;
            case TipoFormulaEnum.ModeloAlternativo:
                return (a.chaveFormulaModelo ?? null) === (b.chaveFormulaModelo ?? null);
            case TipoFormulaEnum.Personalizada:
                return (a.chaveFormulaModelo ?? null) === (b.chaveFormulaModelo ?? null)
                    && (a.expressao ?? '') === (b.expressao ?? '')
                    && (a.tipoArredondamento ?? null) === (b.tipoArredondamento ?? null)
                    && (a.casasDecimais ?? null) === (b.casasDecimais ?? null)
                    && (a.dataVigenciaInicio ?? null) === (b.dataVigenciaInicio ?? null)
                    && (a.dataVigenciaFim ?? null) === (b.dataVigenciaFim ?? null);
            default:
                return false;
        }
    }

    public onEstadoChange(novo: FormulaItemStruct): void {
        const idx = this.estadosLocais.findIndex(e => e.chaveFormula === novo.chaveFormula);
        if (idx >= 0) {
            const copia = [...this.estadosLocais];
            copia[idx] = novo;
            this.estadosLocais = copia;
        }
    }

    public salvar(): void {
        if (this.isValidando) return;
        const erro = this.validarAntesDeSalvar();
        if (erro) {
            this.alertService.show('Atenção', erro, AlertType.warning);
            return;
        }

        const formulas = this.montarFormulasResultado();
        const result: FormulaEditorDialogResult = { formulas };
        // A confirmação precisa validar a fórmula editada junto com as demais alterações ainda
        // mantidas no formulário da entidade. Antes, esse conjunto era usado somente depois do erro,
        // portanto o primeiro diagnóstico podia analisar um grafo incompleto.
        const estados = new Map((this.data.estadosFormulasAgregados ?? []).map(item => [item.chaveFormula, item]));
        formulas.forEach(item => estados.set(item.chaveFormula, item));
        const request = this.montarRequestValidacao([...estados.values()]);
        this.isValidando = true;
        this.data.validarPersonalizacoes(request).subscribe({
                next: response => {
                    this.isValidando = false;
                    if (response.isError || !response.valido) {
                        const erros: FormulaCatalogoErroStruct[] = response.erros ?? [];
                        const erroCiclo = erros.find(item => item.codigo === FormulaErrorCodeEnum.ReferenciaCircular);
                        if (erroCiclo) {
                            if (this.data.permitirConfigurarCiclo === false) {
                                this.alertService.show(
                                    'Referência circular',
                                    erroCiclo.ciclo?.membros?.length ? this.montarMensagemCiclo(erroCiclo.ciclo) : erroCiclo.mensagem,
                                    AlertType.warning,
                                );
                                return;
                            }
                            // O ciclo é configurado no Salvar da tela; aqui a personalização é aceita e só avisa.
                            if (erros.every(item => item.codigo === FormulaErrorCodeEnum.ReferenciaCircular)) {
                                this.alertService.show(
                                    'Referência circular',
                                    'Esta fórmula forma uma referência circular. Ao salvar, você escolhe por onde o cálculo começa.',
                                    AlertType.warning,
                                );
                                this.dialogRef.close(result);
                                return;
                            }
                        }
                        this.alertService.show(
                            'Fórmula inválida',
                            erros.map(item => item.mensagem).filter(Boolean).join('\n') || 'Não foi possível validar as fórmulas.',
                            AlertType.warning,
                        );
                        return;
                    }
                    this.dialogRef.close(result);
                },
                error: () => {
                    this.isValidando = false;
                    this.alertService.show('Erro', 'Não foi possível validar as fórmulas.', AlertType.error);
                },
            });
    }

    private montarFormulasResultado(): FormulaItemStruct[] {
        return this.estadosLocais.map(estado => {
            const inicial = this.data.estadosIniciais.find(x => x.chaveFormula === estado.chaveFormula);
            const idFormulaPersonalizada = inicial?.tipo === estado.tipo ? inicial.idFormulaPersonalizada ?? null : null;
            return { ...estado, idFormulaPersonalizada };
        });
    }

    /** Uma linha com as descrições (até 3) das fórmulas do ciclo, sem chaves técnicas. */
    private montarMensagemCiclo(ciclo: FormulaCicloDetectadoStruct): string {
        const descricoes = [...new Set(ciclo.membros.map(membro => membro.origem?.descricaoFormula).filter(Boolean))].slice(0, 3);
        const lista = descricoes.length > 1 ? ` (${descricoes.slice(0, -1).join(', ')} e ${descricoes[descricoes.length - 1]})` : '';
        return `As fórmulas de custo e venda dependem umas das outras${lista}. Ajuste uma das expressões.`;
    }

    private montarRequestValidacao(formulas: FormulaItemStruct[]): FormulaCatalogoValidarPersonalizacoesRequest {
        const request = new FormulaCatalogoValidarPersonalizacoesRequest();
        request.contextoRaiz = this.data.contextoRaiz;

        for (const formula of this.data.formulas) {
            for (const [chave, valores] of Object.entries(formula.criteriosContexto ?? {})) {
                const valoresAtuais = request.criterios[chave] ?? [];
                request.criterios[chave] = [...new Set([...valoresAtuais, ...valores])];
            }
        }

        request.formulas = formulas.map(item => {
            const formula = new FormulaCatalogoPersonalizacaoRequest();
            formula.chaveFormula = item.chaveFormula;
            formula.chaveFormulaModelo = item.tipo === TipoFormulaEnum.ModeloAlternativo
                ? item.chaveFormulaModelo ?? null
                : null;
            formula.expressaoPersonalizada = item.tipo === TipoFormulaEnum.Personalizada
                ? item.expressao ?? ''
                : null;
            return formula;
        });
        return request;
    }

    public fechar(): void {
        this.dialogRef.close(null);
    }

    private validarAntesDeSalvar(): string | null {
        for (const estado of this.estadosLocais) {
            if (estado.tipo !== TipoFormulaEnum.Personalizada) continue;

            const formula = this.formulasPersonalizaveis.find(f => f.chaveFormula === estado.chaveFormula);
            const variaveis = formula?.variaveis.map(v => v.chave || v.tagXml || v.nome) ?? [];
            const variaveisBooleanas = formula?.variaveis
                .filter(v => v.tipoDadoVariavel === TipoDadoVariavelEnum.Booleano)
                .map(v => v.chave || v.tagXml || v.nome) ?? [];
            const validacao = validarExpressaoFormula(estado.expressao ?? '', variaveis, variaveisBooleanas);
            if (!validacao.valida) {
                return `${formula?.descricao ?? 'Fórmula'}: ${validacao.mensagem}`;
            }

            if (this.data.mostrarVigencia !== false && !estado.dataVigenciaInicio) {
                return `${formula?.descricao ?? 'Fórmula'}: informe a vigência inicial da fórmula personalizada.`;
            }

            if (this.data.mostrarVigencia !== false && estado.dataVigenciaInicio && estado.dataVigenciaFim && estado.dataVigenciaFim < estado.dataVigenciaInicio) {
                return `${formula?.descricao ?? 'Fórmula'}: a vigência final não pode ser menor que a inicial.`;
            }

            const casasDecimais = estado.casasDecimais ?? 2;
            if (casasDecimais < 1 || casasDecimais > 6) {
                return `${formula?.descricao ?? 'Fórmula'}: informe casas decimais entre 1 e 6.`;
            }
        }

        return null;
    }

}
